/*
 *  -----------------------------------------------------------------------
 *    <copyright file="assign-svt-assessment/index.tsx" company="Triumph Research Intelligence">
 *        Copyright (C) 2023 by Triumph Research Intelligence. All rights reserved.
 *    </copyright>
 *   -----------------------------------------------------------------------
 *
 */

import React, { useState } from 'react';
import { useMutation, useQuery } from '@apollo/client/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ASSESSMENTS_MODAL_CLOSE_ACTIONS } from '@components/admin/assessments-svt-grid/assessments-svt-grid.data';
import { ASSIGN_SVT_ASSESSMENT_COLUMNS } from '@components/admin/assign-svt-assessment/assign-svt-assessment.data';
import { AssignSVTAssessmentDialogProps } from '@components/admin/assign-svt-assessment/assign-svt-assessment.interface';
import LibraryGrid from '@components/admin/library-grid';
import Loading from '@components/core/loading';
import { formAssessmentAttributes } from './assign-svt-assessment.helper';
import { useTranslation } from 'hooks';
import { GET_STUDY_CRF_ASSESSMENTS } from 'queries/study-crfs';
import { GET_STUDY_VISIT_SCHEDULES, SAVE_STUDY_VISIT_SCHEDULE_VISIT } from 'queries/study-visit-schedules';
import Button from 'views/components/core/buttons';
import { CustomDialog, DialogActions, DialogContent, DialogTitle } from 'views/components/core/dialog';
import { StudyAssessmentFields } from '@queries/generated/fragments.generated';

const AssignSVTAssessmentDialog = ({
    currentVisit,
    currentScheduleId,
    studyId,
    close,
}: AssignSVTAssessmentDialogProps) => {
    const { t } = useTranslation();

    const [selectedAssessments, setSelectedAssessments] = useState<StudyAssessmentFields[]>([]);

    const { data, loading } = useQuery(GET_STUDY_CRF_ASSESSMENTS, { variables: { studyId } });
    const [saveVisit, { loading: saving }] = useMutation(SAVE_STUDY_VISIT_SCHEDULE_VISIT);

    let assessments: StudyAssessmentFields[] = [];

    const onClose = (action?: string | any) => {
        setSelectedAssessments([]);
        close(action);
    };

    const assignItems = (closeModal?: boolean) => {
        let ids: string[];
        ids = selectedAssessments.map((r) => r?.id);
        // NOTE: backend should provide a decent type for eventIds
        ids = currentVisit.eventIds.concat(ids);

        const submitData = {
            studyId,
            visitScheduleId: currentScheduleId,
            visit: {
                id: currentVisit.id,
                active: currentVisit.active,
                sequenceNumber: currentVisit.sequenceNumber,
                type: currentVisit.type,
                visitId: currentVisit.visitId,
                visitName: currentVisit.visitName,
                leadVisitId: currentVisit.leadVisitId,
                leadPeriod: currentVisit.leadPeriod,
                leadUnit: currentVisit.leadUnit,
                minWinPeriod: currentVisit.minWinPeriod,
                maxWinPeriod: currentVisit.maxWinPeriod,
                minWinUnit: currentVisit.minWinUnit,
                minOccurrences: currentVisit.minOccurrences,
                forecastEndDateField: currentVisit.forecastEndDateField,
                isEndDateEarliest: currentVisit.isEndDateEarliest,
                eventIds: ids,
            },
        };

        saveVisit({
            variables: {
                ...submitData,
            },
            refetchQueries: [
                {
                    query: GET_STUDY_VISIT_SCHEDULES,
                    variables: {
                        studyId,
                    },
                },
            ],
        })
            .then((resp) => {
                setSelectedAssessments([]);
                if (resp.data?.saveStudyVisitScheduleVisit) {
                    onClose({
                        action: closeModal
                            ? ASSESSMENTS_MODAL_CLOSE_ACTIONS.SUCCESS_AND_CLOSE
                            : ASSESSMENTS_MODAL_CLOSE_ACTIONS.SUCCESS,
                        amount: selectedAssessments.length,
                    });
                } else {
                    onClose(ASSESSMENTS_MODAL_CLOSE_ACTIONS.ERROR);
                }
            })
            .catch(() => onClose(ASSESSMENTS_MODAL_CLOSE_ACTIONS.ERROR));
    };

    const getAvailableAssessments = (assessmentItems: StudyAssessmentFields[]) => {
        let filteredAssessments = assessmentItems;
        if (currentVisit?.eventIds?.length) {
            // NOTE: backend should provide a decent type for eventIds
            filteredAssessments = filteredAssessments.filter(
                (assessment) => currentVisit.eventIds.indexOf(assessment.id) < 0,
            );
        }

        return filteredAssessments.map((assessment) => ({
            ...assessment,
            _attributes: formAssessmentAttributes(assessment),
        }));
    };

    if (!studyId || loading) return <Loading float />;

    assessments = getAvailableAssessments(data.crfAssessments);

    return (
        <CustomDialog
            open
            onClose={() => onClose()}
            fullWidth
            maxWidth="md"
            scroll="paper"
            className="custom-width-popup max-w700"
        >
            <DialogTitle id="assign-assessment-modal-title">
                {t('admin:assignAssessments')}
                <FontAwesomeIcon
                    icon={['fal', 'times']}
                    className="fa-lg close-icon-cv"
                    onClick={() => onClose()}
                    data-testid="close-dialog"
                />
            </DialogTitle>

            <DialogContent className="add-risk-dialog-content pb-0 mb-20">
                {currentVisit && (
                    <div className="content-text">
                        <span className="title">{t('subject:visitName')}:</span>
                        {currentVisit.visitName}
                    </div>
                )}
                <LibraryGrid
                    items={assessments}
                    columns={ASSIGN_SVT_ASSESSMENT_COLUMNS(t)}
                    itemsType={t('admin:assessmentRecord-s')}
                    initialSort={[{ field: 'name', dir: 'asc' }]}
                    selection={{
                        selectedItems: selectedAssessments,
                        setSelectedItems: setSelectedAssessments,
                        showItemsCount: true,
                    }}
                />
            </DialogContent>

            <DialogActions className="actions-add-risk assign-items flex-wrap pt-0">
                <Button
                    text="ra:assignRisk"
                    disabled={!selectedAssessments.length || saving}
                    id="assign-assessment-save"
                    onClick={() => assignItems(false)}
                />
                <Button
                    text="ra:assignAndClose"
                    disabled={!selectedAssessments.length || saving}
                    id="assign-assessment-save-close"
                    onClick={() => assignItems(true)}
                />
            </DialogActions>
        </CustomDialog>
    );
};

export default AssignSVTAssessmentDialog;
